'use client';

import type { ApiResponse, PageResponse } from '@/types';

const API_BASE = '/api/v1';

/**
 * Requests go through the same-origin `/api/v1` proxy.
 * The browser forwards the httpOnly auth cookie automatically via `credentials: 'include'`.
 */
async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: HeadersInit = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers ?? {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    // credentials: 'include' forwards the httpOnly cookie for same-origin proxy calls
    credentials: 'include',
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }

  return res.json();
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ProductSpec {
  label: string;
  value: string;
}

export interface ProductResponse {
  id: number;
  name: string;
  slug: string;
  description?: string;
  shortDescription?: string;
  thumbnailUrl?: string;
  images?: string[];
  price: number;
  originalPrice?: number;
  stockQuantity: number;
  soldCount: number;
  featured: boolean;
  active: boolean;
  isHot?: boolean;
  isNew?: boolean;
  sortOrder?: number;
  categoryId?: number;
  categoryName?: string;
  categorySlug?: string;
  // PHYSICAL | DIGITAL
  type: string;
  fileUrl?: string;
  digitalContent?: string;
  ratingAvg?: number;
  ratingCount?: number;
  specs?: ProductSpec[];
  guidance?: string;
  createdAt: string;
}

export interface CategoryResponse {
  id: number;
  name: string;
  slug: string;
  description?: string;
  sortOrder: number;
  /** Số sản phẩm ĐANG BÁN trong danh mục. Trang /shop ẩn nút lọc khi = 0. */
  soSanPham?: number;
}

export interface DiscountValidateResponse {
  valid: boolean;
  code: string;
  discountType?: string;
  discountValue?: number;
  discountAmount?: number;
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  message: string;
}

export interface CreateOrderItem {
  productId: number;
  productName: string;
  productSlug?: string;
  productImage?: string;
  price: number;
  quantity: number;
}

export interface CreateOrderRequest {
  buyerName: string;
  buyerEmail: string;
  buyerPhone?: string;
  buyerAddress?: string;
  shippingProvince?: string;
  items: CreateOrderItem[];
  discountCode?: string;
  notes?: string;
  /**
   * Khoá chống-bấm-hai-lần. Sinh MỘT lần cho mỗi ý định đặt hàng và gửi
   * kèm mọi lần thử lại. Backend có UNIQUE(userId, key) nên lần gửi thứ
   * hai trả lại ĐÚNG đơn cũ thay vì tạo đơn mới.
   *
   * ⚠️ Sinh khoá mới ở mỗi lần bấm là vô hiệu hoá chốt này.
   */
  idempotencyKey?: string;
}

export interface OrderItemResponse {
  id: number;
  productName: string;
  productSlug?: string;
  productImage?: string;
  price: number;
  quantity: number;
  total: number;
  productType?: string;
  fileUrl?: string;
  digitalContent?: string;
  credentials?: string;
}

export interface DigitalDelivery {
  productName: string;
  type: string;
  fileUrl?: string;
  credentials?: string;
}

export interface DeliveryInfo {
  hasDigitalItems: boolean;
  items: DigitalDelivery[];
  message: string;
}

export interface OrderResponse {
  id: number;
  orderCode: string;
  userId?: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone?: string;
  buyerAddress?: string;
  shippingProvince?: string;
  subtotal: number;
  discountAmount: number;
  discountCode?: string;
  shippingFee?: number;
  total: number;
  status: string;
  orderType?: string;
  fulfillmentStatus?: string;
  trackingNumber?: string;
  shippedAt?: string;
  deliveredAt?: string;
  paymentMethod: string;
  paymentStatus: string;
  paidAt?: string;
  refundAmount?: number | null;
  refundReason?: string | null;
  refundedAt?: string | null;
  items: OrderItemResponse[];
  deliveryInfo?: DeliveryInfo;
  createdAt: string;
}

// ─── Products API ─────────────────────────────────────────────────────────────

export async function getProducts(params?: {
  page?: number;
  size?: number;
  category?: string;
  featured?: boolean;
  search?: string;
  sort?: 'newest' | 'price_asc' | 'price_desc' | 'bestselling';
}): Promise<PageResponse<ProductResponse>> {
  const sp = new URLSearchParams();
  if (params?.page !== undefined) sp.set('page', String(params.page));
  if (params?.size !== undefined) sp.set('size', String(params.size));
  if (params?.category) sp.set('category', params.category);
  if (params?.featured !== undefined) sp.set('featured', String(params.featured));
  if (params?.search) sp.set('search', params.search);
  if (params?.sort) sp.set('sort', params.sort);

  const qs = sp.toString();
  const res = await request<ApiResponse<ProductResponse[]>>(
    `/shop/products${qs ? `?${qs}` : ''}`
  );
  return {
    content: Array.isArray(res.data) ? res.data : [],
    pageNumber: (res as any).pagination?.page ?? 0,
    pageSize: (res as any).pagination?.limit ?? (params?.size ?? 12),
    totalElements: (res as any).pagination?.total ?? (Array.isArray(res.data) ? res.data.length : 0),
    totalPages: (res as any).pagination?.totalPages ?? 1,
    first: ((res as any).pagination?.page ?? 0) === 0,
    last: ((res as any).pagination?.page ?? 0) >= (((res as any).pagination?.totalPages ?? 1) - 1),
  };
}

export async function getFeaturedProducts(): Promise<ProductResponse[]> {
  const res = await request<ApiResponse<ProductResponse[]>>(
    '/shop/products/featured'
  );
  return res.data;
}

export async function getProductBySlug(slug: string): Promise<ProductResponse> {
  const res = await request<ApiResponse<ProductResponse>>(
    `/shop/products/${slug}`
  );
  return res.data;
}

export async function getProductById(id: number): Promise<ProductResponse> {
  const res = await request<ApiResponse<ProductResponse>>(
    `/shop/products/id/${id}`
  );
  return res.data;
}

// ─── Categories API ───────────────────────────────────────────────────────────

export async function getCategories(): Promise<CategoryResponse[]> {
  const res = await request<ApiResponse<CategoryResponse[]>>(
    '/shop/categories'
  );
  return res.data;
}

// ─── Discounts API ────────────────────────────────────────────────────────────

export async function validateDiscount(
  code: string
): Promise<DiscountValidateResponse> {
  try {
    const discount = await request<ApiResponse<any>>(
      `/shop/discount/${code}`
    );
    const data = discount.data;
    return {
      valid: true,
      code: data.code,
      discountType: data.discountType,
      discountValue: Number(data.discountValue),
      minOrderAmount: Number(data.minOrderAmount || 0),
      maxDiscountAmount: data.maxDiscountAmount ? Number(data.maxDiscountAmount) : undefined,
      message: 'Discount code applied successfully.',
    };
  } catch {
    return {
      valid: false,
      code,
      discountType: 'PERCENTAGE',
      discountValue: 0,
      minOrderAmount: 0,
      message: 'Invalid or expired discount code.',
    };
  }
}

// ─── Orders API ──────────────────────────────────────────────────────────────

export async function createOrder(
  data: CreateOrderRequest
): Promise<ApiResponse<OrderResponse>> {
  return request('/shop/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getOrderByCode(
  code: string
): Promise<ApiResponse<OrderResponse>> {
  return request(`/shop/orders/${code}`);
}

/**
 * Create a VNPAY-QR payment for an existing shop order.
 * Returns the VNPay gateway `paymentUrl` to render as a QR code.
 * vnp_TxnRef is `PRODUCT_{orderId}_{ts}` so the backend IPN routes it
 * to the shop (product) branch.
 */
export async function createShopPaymentQr(
  orderId: number
): Promise<ApiResponse<{ paymentUrl: string; txnRef: string; amount: number; orderType: 'PRODUCT' }>> {
  return request('/payments/create-qr', {
    method: 'POST',
    body: JSON.stringify({ orderId, orderType: 'PRODUCT' }),
  });
}

/**
 * Create a PayOS checkout link for an existing shop order (PRIMARY gateway).
 * Returns the hosted `checkoutUrl` — redirect the browser to it. On return,
 * PayOS sends the user to /shop/payment-return which polls the order status.
 */
export async function createShopPayos(
  orderCode: string
): Promise<ApiResponse<{ checkoutUrl: string; qrCode: string; orderCode: string }>> {
  return request('/payments/payos/shop/create', {
    method: 'POST',
    body: JSON.stringify({ orderCode }),
  });
}

// "Sản phẩm tương tự" — same category, most-sold first.
export async function getSimilarProducts(slug: string, limit = 8): Promise<ProductResponse[]> {
  const res = await request<ApiResponse<ProductResponse[]>>(
    `/shop/products/${slug}/similar?limit=${limit}`
  );
  return Array.isArray(res.data) ? res.data : [];
}

// ─── Shipping ─────────────────────────────────────────────────────────────────

export interface ShippingConfig { flatFee: number; freeThreshold: number }

export async function getShippingConfig(): Promise<ShippingConfig> {
  try {
    const res = await request<ApiResponse<ShippingConfig>>('/shop/shipping-config');
    return res.data;
  } catch {
    return { flatFee: 30000, freeThreshold: 500000 };
  }
}

// ─── Check usage (API/activation key) ───────────────────────────────────────────

export interface CheckUsageResult {
  configured: boolean;
  ok?: boolean;
  message?: string;
  usage?: Record<string, any>;
}

export async function checkUsage(apiKey: string): Promise<CheckUsageResult> {
  const res = await request<ApiResponse<CheckUsageResult>>('/shop/check-usage', {
    method: 'POST',
    body: JSON.stringify({ apiKey }),
  });
  return res.data;
}

// ─── Product reviews ───────────────────────────────────────────────────────────

export interface ProductReview {
  id: number;
  rating: number;
  comment?: string | null;
  createdAt: string;
  userId: number;
  userName: string;
  userAvatar?: string | null;
}

export interface ProductReviewsResponse {
  average: number;
  count: number;
  reviews: ProductReview[];
}

export async function getProductReviews(slug: string): Promise<ProductReviewsResponse> {
  const res = await request<ApiResponse<ProductReviewsResponse>>(`/shop/products/${slug}/reviews`);
  return res.data;
}

export async function postProductReview(productId: number, rating: number, comment?: string): Promise<ApiResponse<void>> {
  return request(`/shop/products/${productId}/reviews`, {
    method: 'POST',
    body: JSON.stringify({ rating, comment }),
  });
}

export async function deleteMyReview(reviewId: number): Promise<ApiResponse<void>> {
  return request(`/shop/reviews/${reviewId}`, { method: 'DELETE' });
}

export interface AdminReview {
  id: number;
  rating: number;
  comment?: string | null;
  isApproved: boolean;
  createdAt: string;
  userName: string;
  productName?: string;
  productSlug?: string;
}

export async function adminGetReviews(): Promise<AdminReview[]> {
  const res = await request<ApiResponse<AdminReview[]>>('/shop/admin/reviews');
  return Array.isArray(res.data) ? res.data : [];
}

export async function adminModerateReview(id: number, isApproved: boolean): Promise<ApiResponse<void>> {
  return request(`/shop/admin/reviews/${id}`, { method: 'PATCH', body: JSON.stringify({ isApproved }) });
}

export async function adminDeleteReview(id: number): Promise<ApiResponse<void>> {
  return request(`/shop/admin/reviews/${id}`, { method: 'DELETE' });
}

// ─── Admin fulfillment (physical order shipping lifecycle) ──────────────────────

export async function adminUpdateFulfillment(
  orderId: number,
  data: { fulfillmentStatus?: string; trackingNumber?: string }
): Promise<ApiResponse<OrderResponse>> {
  return request(`/shop/admin/orders/${orderId}/fulfillment`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function getMyOrders(): Promise<ApiResponse<OrderResponse[]>> {
  return request('/shop/orders/my');
}

// ─── Admin API ───────────────────────────────────────────────────────────────

export async function adminCreateProduct(
  data: Partial<ProductResponse>
): Promise<ApiResponse<ProductResponse>> {
  return request('/shop/admin/products', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function adminUpdateProduct(
  id: number,
  data: Partial<ProductResponse>
): Promise<ApiResponse<ProductResponse>> {
  return request(`/shop/admin/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function adminDeleteProduct(
  id: number
): Promise<ApiResponse<void>> {
  return request(`/shop/admin/products/${id}`, {
    method: 'DELETE',
  });
}

// Persist a manual product order (array of ids in the desired order).
export async function adminReorderProducts(ids: number[]): Promise<ApiResponse<void>> {
  return request('/shop/admin/products/reorder', {
    method: 'PATCH',
    body: JSON.stringify({ ids }),
  });
}

// Admin product list — unlike the public endpoint this INCLUDES the digital
// deliverables (fileUrl / digitalContent) so the edit form can load them.
export async function adminGetProducts(size = 200): Promise<ProductResponse[]> {
  const res = await request<ApiResponse<ProductResponse[]>>(`/shop/admin/products?size=${size}`);
  return Array.isArray(res.data) ? res.data : [];
}

// ─── Admin Categories ─────────────────────────────────────────────────────────

export interface AdminCategoryResponse extends CategoryResponse {
  productCount: number;
}

export async function adminGetCategories(): Promise<AdminCategoryResponse[]> {
  const res = await request<ApiResponse<AdminCategoryResponse[]>>('/shop/admin/categories');
  return Array.isArray(res.data) ? res.data : [];
}

export async function adminCreateCategory(
  data: { name: string; description?: string; sortOrder?: number }
): Promise<ApiResponse<CategoryResponse>> {
  return request('/shop/admin/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function adminUpdateCategory(
  id: number,
  data: { name?: string; description?: string; sortOrder?: number }
): Promise<ApiResponse<CategoryResponse>> {
  return request(`/shop/admin/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function adminDeleteCategory(id: number): Promise<ApiResponse<void>> {
  return request(`/shop/admin/categories/${id}`, { method: 'DELETE' });
}

// ─── Admin Orders ─────────────────────────────────────────────────────────────

export async function adminGetOrders(params?: {
  page?: number;
  size?: number;
  status?: string;
}): Promise<PageResponse<OrderResponse>> {
  const sp = new URLSearchParams();
  if (params?.page !== undefined) sp.set('page', String(params.page));
  if (params?.size !== undefined) sp.set('size', String(params.size));
  if (params?.status && params.status !== 'ALL') sp.set('status', params.status);

  const qs = sp.toString();
  const res = await request<ApiResponse<OrderResponse[]>>(`/shop/admin/orders${qs ? `?${qs}` : ''}`);
  return {
    content: Array.isArray(res.data) ? res.data : [],
    pageNumber: (res as any).pagination?.page ?? 0,
    pageSize: (res as any).pagination?.limit ?? (params?.size ?? 100),
    totalElements: (res as any).pagination?.total ?? (Array.isArray(res.data) ? res.data.length : 0),
    totalPages: (res as any).pagination?.totalPages ?? 1,
    first: ((res as any).pagination?.page ?? 0) === 0,
    last: ((res as any).pagination?.page ?? 0) >= (((res as any).pagination?.totalPages ?? 1) - 1),
  };
}

export async function adminUpdateOrderStatus(
  id: number,
  status: string
): Promise<ApiResponse<OrderResponse>> {
  return request(`/shop/admin/orders/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

// ─── Digital key pool (unique-per-buyer credentials) ──────────────────────────
export interface ProductKeyItem {
  id: number;
  content: string;
  status: string; // AVAILABLE | SOLD | DISABLED
  orderItemId?: number | null;
  buyerUserId?: number | null;
  assignedAt?: string | null;
  createdAt: string;
}
export interface ProductKeyCounts { available: number; sold: number; total: number }

export async function adminGetProductKeys(
  productId: number
): Promise<ApiResponse<{ keys: ProductKeyItem[]; counts: ProductKeyCounts }>> {
  return request(`/shop/admin/products/${productId}/keys`);
}

export async function adminAddProductKeys(
  productId: number,
  text: string
): Promise<ApiResponse<{ added: number; counts: ProductKeyCounts }>> {
  return request(`/shop/admin/products/${productId}/keys`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export async function adminDeleteProductKey(
  productId: number,
  keyId: number
): Promise<ApiResponse<{ counts: ProductKeyCounts }>> {
  return request(`/shop/admin/products/${productId}/keys/${keyId}`, { method: 'DELETE' });
}

// Full or partial refund of a PAID shop order. Omit refundAmount for a full refund.
export async function adminRefundOrder(
  id: number,
  data: { reason: string; refundAmount?: number }
): Promise<ApiResponse<OrderResponse>> {
  return request(`/shop/admin/orders/${id}/refund`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ─── Admin Discounts ──────────────────────────────────────────────────────────

export async function adminGetDiscounts() {
  return request<
    ApiResponse<
      Array<{
        id: number;
        code: string;
        discountType: string;
        discountValue: number;
        minOrderAmount: number;
        maxDiscountAmount?: number;
        maxUses?: number;
        usedCount: number;
        active: boolean;
        description?: string;
        expiresAt?: string;
      }>
    >
  >('/shop/admin/discounts');
}

export async function adminCreateDiscount(
  data: Record<string, unknown>
): Promise<ApiResponse<void>> {
  return request('/shop/admin/discounts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function adminUpdateDiscount(
  id: number,
  data: Record<string, unknown>
): Promise<ApiResponse<void>> {
  return request(`/shop/admin/discounts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function adminDeleteDiscount(
  id: number
): Promise<ApiResponse<void>> {
  return request(`/shop/admin/discounts/${id}`, {
    method: 'DELETE',
  });
}

// ─── Map backend product → frontend Product type ─────────────────────────────

export function mapProductFromBackend(bp: ProductResponse) {
  return {
    id: String(bp.id),
    name: bp.name ?? '',
    slug: bp.slug ?? '',
    price: bp.price ?? 0,
    originalPrice: bp.originalPrice,
    thumbnail: bp.thumbnailUrl || '/images/products/default.jpg',
    images: Array.isArray(bp.images) ? bp.images : [],
    category: bp.categoryName || 'Khác',
    productType: bp.type || 'DIGITAL',
    rating: bp.ratingAvg ?? 0,
    reviewCount: bp.ratingCount ?? 0,
    description: bp.shortDescription || bp.description || '',
    features: [],
    specs: bp.specs ?? [],
    guidance: bp.guidance ?? '',
    isHot: bp.isHot ?? false,
    isNew: bp.isNew ?? false,
    sortOrder: bp.sortOrder ?? 0,
    fileUrl: bp.fileUrl,
    digitalContent: bp.digitalContent,
    stock: bp.stockQuantity ?? 0,
    isFeatured: bp.featured ?? false,
    soldCount: bp.soldCount ?? 0,
    createdAt: bp.createdAt,
    tags: [],
  };
}


// ═══════════════════════════════════════════════════════════════════════
// Thanh toán bằng ví · chuyển khoản · tự phục vụ sau bán (13/09/2026)
// ═══════════════════════════════════════════════════════════════════════

export interface BankTransferPayload {
  refCode: string;
  amountVnd: number;
  status: string;
  expiresAt: string | null;
  bank: { name: string | null; bin: string | null; accountNo: string | null; accountName: string | null };
  noiDungChuyenKhoan: string;
  qrString: string;
  note: string | null;
}

/** Thanh toán một đơn PENDING bằng điểm trong ví. */
export async function payShopOrderWithPoints(
  orderCode: string,
): Promise<ApiResponse<{ order: OrderResponse; balance: number }>> {
  return request(`/shop/orders/${orderCode}/pay-with-points`, { method: 'POST' });
}

/** Sinh (hoặc lấy lại) thông tin chuyển khoản cho một đơn PENDING. */
export async function createShopBankTransfer(
  orderCode: string,
): Promise<ApiResponse<BankTransferPayload>> {
  return request('/payments/bank-transfer/shop', {
    method: 'POST',
    body: JSON.stringify({ orderCode }),
  });
}

export async function getBankTransferConfig(): Promise<
  ApiResponse<{
    enabled: boolean; bankName: string | null; bankBin: string | null;
    accountNo: string | null; accountName: string | null; ttlMinutes: number; note: string | null;
  }>
> {
  return request('/payments/bank-transfer/config');
}

export async function getBankTransferStatus(
  refCode: string,
): Promise<ApiResponse<{ refCode: string; status: string; amountVnd: number; confirmedAt: string | null; adminNote: string | null }>> {
  return request(`/payments/bank-transfer/${refCode}`);
}

/** Số dư ví — dùng ở checkout để biết có trả bằng điểm được không. */
export async function getWalletBalance(): Promise<
  ApiResponse<{ balance: number; totalEarned: number; totalSpent: number; pointsPerVnd: number }>
> {
  return request('/wallet');
}

export interface KeyReplacementRow {
  id: number;
  orderCode: string;
  orderItemId: number;
  productName: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  adminNote: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

export async function requestKeyReplacement(
  orderCode: string,
  itemId: number,
  reason: string,
): Promise<ApiResponse<{ id: number; status: string }>> {
  return request(`/shop/orders/${orderCode}/items/${itemId}/key-replacement`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function getMyKeyReplacements(): Promise<ApiResponse<KeyReplacementRow[]>> {
  return request('/shop/key-replacements');
}

export interface ReorderRow {
  productId: number | null;
  name: string;
  slug: string | null;
  image: string | null;
  quantity: number;
  giaCu: number;
  giaMoi: number | null;
  doiGia: boolean;
  conBan: boolean;
  tonKho: number;
  duHang: boolean;
}

export async function getReorderItems(orderCode: string): Promise<ApiResponse<ReorderRow[]>> {
  return request(`/shop/orders/${orderCode}/reorder`);
}

/**
 * Tải hoá đơn PDF.
 *
 * Dùng fetch thẳng chứ không qua `request()` vì đáp trả là PDF nhị phân,
 * `res.json()` sẽ nổ. Trình duyệt tự lưu file qua một thẻ <a download> tạm.
 */
export async function downloadInvoice(orderCode: string): Promise<void> {
  const res = await fetch(`${API_BASE}/shop/orders/${orderCode}/invoice`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `hoa-don-${orderCode}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Nhả URL sau một nhịp — thu hồi ngay lập tức làm hỏng lượt tải ở Safari.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
